import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState, } from "react";
import { getClassName } from "@sscs/utils";
import { Virtuoso, } from "react-virtuoso";
import { SidebarCollapseContext } from "../NavigationSidebar/collapseContext";
import EmptyState from "../NoResultsFound/EmptyState";
import TreeRow from "./TreeRow";
import styles from "./treeView.module.scss";
import { useTreeModel } from "./useTreeModel";
const PresentationList = React.forwardRef(function PresentationList(props, ref) {
    return React.createElement("div", { ref: ref, ...props, role: "presentation" });
});
function PresentationItem({ item, ...props }) {
    void item;
    return React.createElement("div", { ...props, role: "presentation" });
}
const VIRTUOSO_COMPONENTS = { List: PresentationList, Item: PresentationItem };
const isNodeRow = (row) => row.kind === "node";
const TreeView = (props) => {
    var _a;
    const { selected, onChange, title, shortTitle, "aria-label": ariaLabel, defaultExpanded, expanded, onExpandedChange, empty, virtualized = false, scrollToSelected = false, className, style, } = props;
    const treeList = useMemo(() => { var _a, _b; return (_a = props.trees) !== null && _a !== void 0 ? _a : [{ nodes: (_b = props.nodes) !== null && _b !== void 0 ? _b : [] }]; }, [props.trees, props.nodes]);
    const { isCollapsed: compact } = useContext(SidebarCollapseContext);
    const { visibleRows, rowIndexById, separatedRowIndices, toggleExpanded, expandNode, } = useTreeModel({
        trees: treeList,
        selected,
        defaultExpanded,
        expanded,
        onExpandedChange,
    });
    const treeRef = useRef(null);
    const virtuosoRef = useRef(null);
    const titleId = useId();
    const treeDomId = useId();
    const [focusedId, setFocusedId] = useState(null);
    const [pendingFocusId, setPendingFocusId] = useState(null);
    const displayTitle = compact ? (shortTitle !== null && shortTitle !== void 0 ? shortTitle : title) : title;
    const hasTitle = displayTitle !== null && displayTitle !== undefined;
    const getTreeEl = useCallback(() => { var _a; return (_a = treeRef.current) !== null && _a !== void 0 ? _a : document.getElementById(treeDomId); }, [treeDomId]);
    const findNodeIndex = useCallback((start, step) => {
        for (let cursor = start; cursor >= 0 && cursor < visibleRows.length; cursor += step) {
            if (isNodeRow(visibleRows[cursor]))
                return cursor;
        }
        return -1;
    }, [visibleRows]);
    useEffect(() => {
        if (rowIndexById.size === 0) {
            if (focusedId !== null)
                setFocusedId(null);
            return;
        }
        if (focusedId && rowIndexById.has(focusedId))
            return;
        const firstNodeIndex = findNodeIndex(0, 1);
        const fallback = selected && rowIndexById.has(selected)
            ? selected
            : visibleRows[firstNodeIndex].id;
        setFocusedId(fallback);
    }, [focusedId, selected, visibleRows, rowIndexById, findNodeIndex]);
    const onFocusRow = useCallback((id) => setFocusedId(id), []);
    const onPendingFocusHandled = useCallback(() => setPendingFocusId(null), []);
    const focusRowByIndex = useCallback((index) => {
        var _a;
        const target = visibleRows[index];
        if (!target || !isNodeRow(target))
            return;
        const targetId = target.id;
        setFocusedId(targetId);
        const focusNow = () => {
            var _a;
            const element = (_a = getTreeEl()) === null || _a === void 0 ? void 0 : _a.querySelector(`[data-node-id="${CSS.escape(targetId)}"]`);
            if (element) {
                element.focus();
                return true;
            }
            return false;
        };
        if (focusNow())
            return;
        setPendingFocusId(targetId);
        (_a = virtuosoRef.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex({
            index,
            align: "center",
            behavior: "auto",
        });
    }, [visibleRows, getTreeEl]);
    const handleKeyDown = useCallback((event) => {
        var _a;
        var _b;
        const rowElement = event.target.closest('[role="treeitem"]');
        const nodeId = rowElement === null || rowElement === void 0 ? void 0 : rowElement.getAttribute("data-node-id");
        if (!nodeId)
            return;
        const index = rowIndexById.get(nodeId);
        if (index === undefined)
            return;
        const row = visibleRows[index];
        if (!isNodeRow(row))
            return;
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                focusRowByIndex(findNodeIndex(index + 1, 1));
                break;
            case "ArrowUp":
                event.preventDefault();
                focusRowByIndex(findNodeIndex(index - 1, -1));
                break;
            case "Home":
                event.preventDefault();
                focusRowByIndex(findNodeIndex(0, 1));
                break;
            case "End":
                event.preventDefault();
                focusRowByIndex(findNodeIndex(visibleRows.length - 1, -1));
                break;
            case "ArrowRight":
                if (row.hasChildren && !row.expanded) {
                    event.preventDefault();
                    toggleExpanded(nodeId);
                }
                else if (row.hasChildren && row.expanded) {
                    event.preventDefault();
                    focusRowByIndex(findNodeIndex(index + 1, 1));
                }
                break;
            case "ArrowLeft":
                if (row.hasChildren && row.expanded) {
                    event.preventDefault();
                    toggleExpanded(nodeId);
                }
                else if (row.parentId) {
                    const parentIndex = rowIndexById.get(row.parentId);
                    if (parentIndex !== undefined) {
                        event.preventDefault();
                        focusRowByIndex(parentIndex);
                    }
                }
                break;
            case "Enter":
            case " ":
                event.preventDefault();
                if (row.hasChildren) {
                    toggleExpanded(nodeId);
                }
                else if (!row.node.disabled) {
                    const labelEl = rowElement === null || rowElement === void 0 ? void 0 : rowElement.querySelector("[data-tree-label]");
                    (_a = ((_b = labelEl === null || labelEl === void 0 ? void 0 : labelEl.querySelector("a")) !== null && _b !== void 0 ? _b : labelEl)) === null || _a === void 0 ? void 0 : _a.click();
                }
                break;
            default:
                break;
        }
    }, [visibleRows, rowIndexById, focusRowByIndex, findNodeIndex, toggleExpanded]);
    const selectedIndex = selected ? ((_a = rowIndexById.get(selected)) !== null && _a !== void 0 ? _a : -1) : -1;
    const selectedRow = selectedIndex >= 0 ? visibleRows[selectedIndex] : undefined;
    const selectedParentId = selectedRow && isNodeRow(selectedRow) ? selectedRow.parentId : null;
    const didInitialScrollRef = useRef(false);
    useEffect(() => {
        var _a;
        if (!virtualized || !scrollToSelected || didInitialScrollRef.current) {
            return;
        }
        if (selectedIndex < 0)
            return;
        didInitialScrollRef.current = true;
        (_a = virtuosoRef.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex({
            index: selectedIndex,
            align: "center",
            behavior: "auto",
        });
    }, [virtualized, scrollToSelected, selectedIndex]);
    const renderRow = useCallback((row, index) => {
        var _a;
        if (!isNodeRow(row)) {
            return (React.createElement("div", { key: row.key, role: "presentation", className: getClassName(styles.title, {
                    [styles["tree-separated"]]: separatedRowIndices.has(index),
                }) },
                React.createElement("span", { className: getClassName(styles["title-text"], {
                        [styles["title-text-compact"]]: compact,
                    }) }, compact ? ((_a = row.shortTitle) !== null && _a !== void 0 ? _a : row.title) : row.title),
                React.createElement("span", { "aria-hidden": true, className: styles["title-rule"] })));
        }
        return (React.createElement(TreeRow, { key: row.id, row: row, selected: selected, selectedParentId: selectedParentId, focusedId: focusedId, pendingFocusId: pendingFocusId, compact: compact, separated: separatedRowIndices.has(index), onSelect: onChange, onToggle: toggleExpanded, onExpand: expandNode, onFocusRow: onFocusRow, onPendingFocusHandled: onPendingFocusHandled }));
    }, [
        selected,
        selectedParentId,
        focusedId,
        pendingFocusId,
        compact,
        separatedRowIndices,
        onChange,
        toggleExpanded,
        expandNode,
        onFocusRow,
        onPendingFocusHandled,
    ]);
    const ariaLabelledBy = !ariaLabel && hasTitle ? titleId : undefined;
    const isEmpty = empty !== undefined && rowIndexById.size === 0;
    const titleRow = hasTitle && (React.createElement("div", { id: titleId, "data-tree-title": true, className: getClassName(styles.title, styles["title-sticky"]) },
        React.createElement("span", { className: getClassName(styles["title-text"], {
                [styles["title-text-compact"]]: compact,
            }) }, displayTitle),
        React.createElement("span", { "aria-hidden": true, className: styles["title-rule"] })));
    if (isEmpty) {
        return (React.createElement(React.Fragment, null,
            titleRow,
            React.createElement("div", { className: getClassName(styles.empty, className) },
                React.createElement(EmptyState, { ...empty }))));
    }
    return (React.createElement(React.Fragment, null,
        titleRow,
        virtualized ? (React.createElement(Virtuoso, { ref: virtuosoRef, id: treeDomId, role: "tree", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, className: className, style: style, onKeyDown: handleKeyDown, data: visibleRows, components: VIRTUOSO_COMPONENTS, computeItemKey: (_index, row) => (isNodeRow(row) ? row.id : row.key), itemContent: (index, row) => renderRow(row, index), initialTopMostItemIndex: scrollToSelected && selectedIndex >= 0
                ? { index: selectedIndex, align: "center", behavior: "auto" }
                : 0, increaseViewportBy: 200 })) : (React.createElement("div", { ref: treeRef, id: treeDomId, role: "tree", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, className: getTreeClassName(className), style: style, onKeyDown: handleKeyDown }, visibleRows.map((row, index) => renderRow(row, index))))));
};
function getTreeClassName(className) {
    return className ? `${styles.tree} ${className}` : styles.tree;
}
export default TreeView;
//# sourceMappingURL=TreeView.js.map